import {
  emptyProductAttributeSortedSet,
  PriceRangeNullable,
  ProductAttributeSortedSet,
} from '../product/filter/backend/productFilters';
import { Effect, Schema } from 'effect';

import { overrideSchema } from '../openapi/overrideSchema';
// Branded related-article element for the nested articles array (WS7 Task 7a).
import { ArticleIndexFavourite } from '../branded/ContentSchema';
// Reuse the existing `BrandUuid` value-object brand for the brand id (WS7:
// never re-declare a brand). It is the same domain value as the filter-state
// brand uuid.
import { BrandUuid } from '../product/filter/state/brand';

const JsonLdContext = Schema.optionalKey(
  Schema.Union(
    [
      Schema.String,
      Schema.Struct({
        '@vocab': Schema.String,
        hydra: Schema.Literal('http://www.w3.org/ns/hydra/core#'),
      }),
    ],
    { mode: 'oneOf' }
  ).annotate({ readOnly: true })
);
const JsonLdIri = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const JsonLdType = Schema.optionalKey(Schema.String.annotate({ readOnly: true }));
const NullableString = Schema.Union([Schema.String, Schema.Null]);
const ReadOnlyInt = Schema.optionalKey(
  Schema.Finite.annotate({ readOnly: true }).check(Schema.isInt())
);

const BrandImage = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: ReadOnlyInt,
  pathAbstract: Schema.optionalKey(NullableString.annotate({ readOnly: true })),
});

export const BrandIndex = Schema.Struct({
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.String.annotate({ format: 'uuid' }),
  code: Schema.String,
  priority: Schema.Finite.check(Schema.isInt()),
  page: Schema.Literals(['yes', 'hidden', 'no']),
  image: Schema.Union([BrandImage, Schema.Null]),
  name: NullableString,
  metaTitle: NullableString,
  routerUrl: Schema.String.annotate({ readOnly: true }),
});
export type BrandIndex = Schema.Schema.Type<typeof BrandIndex>;

export const BrandIndexDetail = Schema.Struct({
  ...BrandIndex.fields,
  totalProducts: Schema.optionalKey(Schema.Finite.check(Schema.isInt())),
});
export type BrandIndexDetail = Schema.Schema.Type<typeof BrandIndexDetail>;

const BrandDetailBase = Schema.Struct({
  '@context': JsonLdContext,
  '@id': JsonLdIri,
  '@type': JsonLdType,
  id: Schema.String.annotate({ format: 'uuid' }),
  code: Schema.String,
  priority: Schema.Finite.check(Schema.isInt()),
  page: Schema.Literals(['yes', 'hidden', 'no']),
  image: Schema.Union([BrandImage, Schema.Null]),
  name: NullableString,
  description: NullableString,
  metaTitle: NullableString,
  metaDescription: NullableString,
  attributes: Schema.optionalKey(Schema.Array(Schema.Unknown).annotate({ readOnly: true })),
  priceRange: Schema.optionalKey(Schema.Unknown.annotate({ readOnly: true })),
  routerUrl: Schema.String.annotate({ readOnly: true }),
  articles: Schema.optionalKey(Schema.Array(ArticleIndexFavourite).annotate({ readOnly: true })),
});

// Same shape as `TaxonExpandedResponse`: hand-tuned default values for
// rcprofi-specific fields, keys constrained against the generated `BrandDetail`
// via `overrideSchema`. See sibling `brand.tst.ts` for the drift assertions.
//
// WS7 branded core contract: each branded field wraps the field's EXACT
// generated schema (Encoded unchanged, decoded value byte-identical). `id`
// reuses `BrandUuid` (keeping the `format: 'uuid'` annotation); the `@id` IRI
// carries a plain-string `Brand.iri` brand — NOT iri.ts's `BrandID` / `BrandIRI`
// parser value objects. BrandDetail has no `slug` field.
export const BrandExpandedResponse = overrideSchema(BrandDetailBase, {
  attributes: ProductAttributeSortedSet.pipe(
    Schema.withDecodingDefaultType(Effect.succeed(emptyProductAttributeSortedSet))
  ),
  priceRange: PriceRangeNullable.pipe(Schema.withDecodingDefaultType(Effect.succeed(null))),
  '@id': Schema.optionalKey(
    Schema.String.annotate({ readOnly: true }).pipe(Schema.brand('Brand.iri'))
  ),
  id: BrandUuid.annotate({ format: 'uuid' }),
  code: Schema.String.pipe(Schema.brand('Brand.code')),
  name: Schema.Union([Schema.String.pipe(Schema.brand('Brand.name')), Schema.Null]),
  routerUrl: Schema.String.annotate({
    description: 'Frontend router URL for this resource.',
    readOnly: true,
  }).pipe(Schema.brand('Brand.routerUrl')),
  articles: Schema.optionalKey(Schema.Array(ArticleIndexFavourite).annotate({ readOnly: true })),
}).annotate({ identifier: 'BrandExpandedResponse' });

export type BrandExpandedResponse = Schema.Schema.Type<typeof BrandExpandedResponse>;
